// Monatsabschluss im Widget-Stil (design_handoff_personal §6, Screens 16–18, 22, 26): Mitarbeiter-Karten,
// Lohnarten-Widget und Auszahl-Dialog. Rechnen, Exportieren und Speichern bleiben in der Seite bzw. lib/zeiterfassung.
import { useState } from 'react';
import {
  AirplaneTilt, Check, Confetti, CurrencyEur, FirstAid, ListNumbers, LockSimple, Moon, PiggyBank, ShieldCheck, Sun, Warning, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import { DialogHuelle, ImmoButton } from '../../../../components/immobilien/ui';
import { IosSchalter } from '../../../../components/ui/IosSchalter';
import { stundenText } from '../../../../lib/zeiterfassung/berechnung';
import { euroText } from '../../../../lib/zeiterfassung/verguetung';
import type { Lohnart } from '../../../../lib/zeiterfassung/lohnexport';
import type { Verstoss } from '../../../../lib/zeiterfassung/zuschlaege';
import { auszahlVorschlag, initialen, pruefeAuszahlung } from '../../../../lib/personalAnsicht/darstellung';

// ─── Mitarbeiter-Karte (16) ─────────────────────────────────────────────────────

export interface KartenZeile {
  id: string; name: string; ist: number; soll: number; saldo: number; nacht: number; sonntag: number; feiertag: number;
  urlaubstage: number; kranktage: number; verstoesse: Verstoss[]; konto: number; brutto: number | null;
  abschluss: { abgeschlossen_am: string } | null;
}

function Merkmal({ icon: I, text, farbe, flaeche, tip }: { icon: Icon; text: string; farbe: string; flaeche: string; tip?: string }) {
  return (
    <span data-tip={tip} className="inline-flex items-center whitespace-nowrap"
      style={{ height: 24, padding: '0 10px', gap: 5, borderRadius: 12, fontSize: 11.5, fontWeight: 800, background: flaeche, color: farbe }}>
      <I size={12} weight="fill" />{text}
    </span>
  );
}

const PILLE = { height: 32, padding: '0 12px', gap: 6, borderRadius: 16, border: 'none', fontFamily: 'inherit', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' } as const;

export function MonatKarte({ z, stundensatz, warnungenOffen, arbeitet, onWarnungen, onAuszahlen, onAbschliessen, onAufheben }: {
  z: KartenZeile; stundensatz: number | undefined; warnungenOffen: boolean; arbeitet: boolean;
  onWarnungen: () => void; onAuszahlen: () => void; onAbschliessen: () => void; onAufheben: () => void;
}) {
  const fertig = !!z.abschluss;
  const saldoFarbe = z.saldo > 0 ? 'var(--ok)' : z.saldo < 0 ? 'var(--warn)' : 'var(--ink3)';
  const merkmale = z.nacht > 0 || z.sonntag > 0 || z.feiertag > 0 || z.urlaubstage > 0 || z.kranktage > 0 || z.brutto != null;
  return (
    <div className="immo-glas flex flex-col" style={{ borderRadius: 24, padding: '14px 16px', gap: 10, boxShadow: fertig ? 'var(--glass-sh), inset 0 0 0 1.5px var(--ok-soft)' : undefined }}>
      <div className="flex flex-wrap items-center" style={{ gap: 14 }}>
        <span className="flex shrink-0 items-center justify-center"
          style={{ width: 44, height: 44, borderRadius: '50%', fontSize: 14, fontWeight: 800, background: fertig ? 'var(--ok-soft)' : 'var(--acc-soft)', color: fertig ? 'var(--ok)' : 'var(--acc)' }}>
          {initialen(z.name) || '?'}
        </span>
        <div className="flex min-w-0 flex-col" style={{ flex: '1 1 200px', gap: 4 }}>
          <span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{z.name}</span>
          <div className="flex flex-wrap" style={{ gap: 10, fontSize: 12.5, color: 'var(--ink2)', fontVariantNumeric: 'tabular-nums' }}>
            <span>Ist <b style={{ color: 'var(--ink)' }}>{stundenText(z.ist)}</b></span>
            <span>Soll <b style={{ color: 'var(--ink)' }}>{stundenText(z.soll)}</b></span>
            <span>Saldo <b style={{ color: saldoFarbe }}>{stundenText(z.saldo, true)}</b></span>
          </div>
        </div>
        {z.verstoesse.length > 0 && (
          <button type="button" data-tip="Hinweise Arbeitszeitgesetz – Antippen für Details" aria-expanded={warnungenOffen} onClick={onWarnungen}
            className="immo-tippziel inline-flex items-center" style={{ ...PILLE, cursor: 'pointer', background: 'var(--warn-soft)', color: 'var(--warn)' }}>
            <Warning size={14} weight="fill" />{z.verstoesse.length}
          </button>
        )}
        <span data-tip="Stundenkonto gesamt (nach Auszahlungen)" className="inline-flex items-center"
          style={{ ...PILLE, fontVariantNumeric: 'tabular-nums', background: 'var(--fill2)', color: z.konto >= 0 ? 'var(--ok)' : 'var(--bad)' }}>
          <PiggyBank size={14} weight="fill" />Konto {stundenText(z.konto, true)}
        </span>
        {!fertig && z.konto > 0 && (
          <button type="button" data-tip="Überstunden auszahlen" onClick={onAuszahlen} disabled={arbeitet}
            className="immo-tippziel inline-flex items-center" style={{ ...PILLE, cursor: 'pointer', background: 'var(--info-soft)', color: 'var(--info)', opacity: arbeitet ? 0.5 : 1 }}>
            <CurrencyEur size={13} weight="bold" />Auszahlen
          </button>
        )}
        {fertig ? (
          <button type="button" onClick={onAufheben} disabled={arbeitet}
            data-tip={`Abgeschlossen am ${new Date(z.abschluss!.abgeschlossen_am).toLocaleDateString('de-DE')} – Antippen hebt den Abschluss auf`}
            className="immo-tippziel inline-flex items-center" style={{ ...PILLE, height: 36, padding: '0 14px', borderRadius: 18, fontSize: 12.5, cursor: 'pointer', background: 'var(--ok-soft)', color: 'var(--ok)', opacity: arbeitet ? 0.5 : 1 }}>
            <LockSimple size={14} weight="fill" />Abgeschlossen
          </button>
        ) : (
          <button type="button" onClick={onAbschliessen} disabled={arbeitet}
            data-tip="Abschließen – Monat sperren und Werte für die Lohnabrechnung festschreiben"
            className="immo-tippziel inline-flex items-center" style={{ ...PILLE, height: 36, padding: '0 16px', borderRadius: 18, fontSize: 12.5, cursor: 'pointer',
              background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)', opacity: arbeitet ? 0.5 : 1 }}>
            <ShieldCheck size={14} weight="bold" />Abschließen
          </button>
        )}
      </div>
      {merkmale && (
        <div className="flex flex-wrap" style={{ gap: 6, marginLeft: 'min(58px, 10%)' }}>
          {z.nacht > 0 && <Merkmal icon={Moon} text={`Nacht ${stundenText(z.nacht)}`} farbe="var(--ink2)" flaeche="var(--fill2)" tip="Nachtstunden – 23 bis 6 Uhr, zuschlagspflichtig" />}
          {z.sonntag > 0 && <Merkmal icon={Sun} text={`Sonntag ${stundenText(z.sonntag)}`} farbe="var(--ink2)" flaeche="var(--fill2)" tip="Sonntagsstunden – zuschlagspflichtig" />}
          {z.feiertag > 0 && <Merkmal icon={Confetti} text={`Feiertag ${stundenText(z.feiertag)}`} farbe="var(--ink2)" flaeche="var(--fill2)" tip="Feiertagsstunden – zuschlagspflichtig" />}
          {z.urlaubstage > 0 && <Merkmal icon={AirplaneTilt} text={`${z.urlaubstage.toLocaleString('de-DE')} Urlaub`} farbe="var(--ok)" flaeche="var(--ok-soft)" tip="Urlaubstage in diesem Monat" />}
          {z.kranktage > 0 && <Merkmal icon={FirstAid} text={`${z.kranktage.toLocaleString('de-DE')} krank`} farbe="var(--bad)" flaeche="var(--bad-soft)" tip="Kranktage in diesem Monat" />}
          {z.brutto != null && (
            <Merkmal icon={CurrencyEur} text={`≈ ${euroText(z.brutto)}`} farbe="var(--acc)" flaeche="var(--acc-soft)"
              tip={`Ist-Stunden × ${euroText(stundensatz ?? 0)} — reine Rechenhilfe, ohne Zuschläge und Abzüge`} />
          )}
        </div>
      )}
      {warnungenOffen && (
        <div className="immo-pop-in flex flex-col" style={{ marginLeft: 'min(58px, 10%)', borderRadius: 16, padding: '10px 12px', gap: 6, background: 'var(--warn-soft)' }}>
          {z.verstoesse.map((v, i) => (
            <span key={i} style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              <b style={{ fontWeight: 800 }}>{new Date(`${v.tag}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}:</b> {v.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Lohnarten für DATEV (17) ───────────────────────────────────────────────────

const FELD = {
  height: 44, padding: '0 12px', borderRadius: 13, border: 'none', outline: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)',
  fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--ink)', minWidth: 0,
} as const;

export function LohnartenWidget({ lohnarten, datev, onDatev, onNummer, onAktiv, onSchliessen }: {
  lohnarten: Lohnart[]; datev: { beraternummer: string | null; mandantennummer: string | null };
  onDatev: (schluessel: 'beraternummer' | 'mandantennummer', wert: string) => void;
  onNummer: (la: Lohnart, nummer: string) => void; onAktiv: (la: Lohnart, aktiv: boolean) => void; onSchliessen: () => void;
}) {
  return (
    <div className="immo-glas immo-pop-in flex flex-col" style={{ borderRadius: 26, padding: 18, gap: 12 }}>
      <div className="flex items-center justify-between" style={{ gap: 8 }}>
        <span className="flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}>
          <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: 'var(--acc-soft)' }}>
            <ListNumbers size={15} weight="fill" color="var(--acc)" />
          </span>
          Lohnarten für DATEV
        </span>
        <button data-tip="Schließen – Lohnarten ausblenden" className="immo-tippziel" type="button" onClick={onSchliessen}
          style={{ height: 32, padding: '0 12px', borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
          schließen
        </button>
      </div>
      <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))' }}>
        {([['beraternummer', 'Beraternummer'], ['mandantennummer', 'Mandantennummer']] as const).map(([k, l]) => (
          <label key={k} className="flex flex-col" style={{ gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{l}</span>
            <input defaultValue={datev[k] || ''} onBlur={(e) => onDatev(k, e.target.value)} style={FELD} />
          </label>
        ))}
      </div>
      <div className="flex flex-col overflow-hidden" style={{ borderRadius: 18, background: 'var(--fill)' }}>
        {lohnarten.map((la) => (
          <div key={la.schluessel} className="flex flex-wrap items-center" style={{ gap: 12, padding: '8px 12px', borderBottom: '1px solid var(--line)' }}>
            <span className="min-w-0 flex-1" style={{ fontSize: 13.5, fontWeight: 700, minWidth: 120 }}>{la.bezeichnung}</span>
            <input defaultValue={la.nummer || ''} placeholder="Lohnart-Nr." aria-label={`Lohnart-Nummer ${la.bezeichnung}`}
              onBlur={(e) => onNummer(la, e.target.value)}
              style={{ ...FELD, width: 110, height: 36, borderRadius: 11, padding: '0 10px', fontSize: 13,
                boxShadow: la.nummer ? 'inset 0 0 0 1px var(--line)' : 'inset 0 0 0 1.5px var(--warn)' }} />
            <span className="flex items-center" style={{ gap: 8, fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>
              <IosSchalter an={la.aktiv} label={`${la.bezeichnung} exportieren`} tip="Exportieren – Lohnart im DATEV-Export berücksichtigen"
                onChange={(an) => onAktiv(la, an)} />
              exportieren
            </span>
          </div>
        ))}
      </div>
      <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--ink3)' }}>
        Die Nummern kommen von Ihrem Steuerberater und sind je Kanzlei verschieden. Lohnarten ohne
        Nummer bleiben im Export weg, statt mit einer geratenen Nummer zu laufen. Personalnummern
        werden aus der Personalakte gezogen.
      </span>
    </div>
  );
}

// ─── Überstunden auszahlen (18, ersetzt prompt + confirm) ───────────────────────

/**
 * Bisher: prompt „Überstunden auszahlen für …“ mit Vorschlag, bei mehr als dem Guthaben ein zweites
 * confirm. Jetzt ein Dialog: die zweite Bestätigung ist der Knopf „Trotzdem auszahlen“ unter dem
 * gleichen Warntext. Ungültige Zahl → „Bitte eine Stundenzahl angeben“.
 */
export function AuszahlDialog({ name, konto, zumText, monatText, laeuft, onAuszahlen, onAbbrechen }: {
  name: string; konto: number; zumText: string; monatText: string; laeuft: boolean;
  onAuszahlen: (minuten: number) => void; onAbbrechen: () => void;
}) {
  const [eingabe, setEingabe] = useState(() => auszahlVorschlag(konto));
  const [fehler, setFehler] = useState<string | null>(null);
  const pruefung = pruefeAuszahlung(eingabe.trim(), konto);
  const ueber = pruefung.ok && pruefung.ueberGuthaben;

  const los = () => {
    if (!pruefung.ok) { if (pruefung.fehler) setFehler(pruefung.fehler); return; }
    onAuszahlen(pruefung.minuten);
  };

  return (
    <DialogHuelle titel="Überstunden auszahlen" icon={CurrencyEur} onSchliessen={onAbbrechen} laeuft={laeuft} breite={460}
      fuss={<div className="flex w-full justify-end" style={{ gap: 10 }}>
        <ImmoButton groesse={44} onClick={onAbbrechen} disabled={laeuft}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" groesse={44} icon={ueber ? Warning : Check} onClick={los} disabled={laeuft || !eingabe.trim()}
          style={ueber ? { background: 'var(--warn)', color: '#fff', boxShadow: 'none' } : undefined}
          data-tip={ueber ? 'Trotzdem auszahlen – Das Konto geht ins Minus' : 'Auszahlen – Stunden vom Konto abziehen'}>
          {ueber ? 'Trotzdem auszahlen' : 'Auszahlen'}
        </ImmoButton>
      </div>}>
      <div className="flex items-center justify-between" style={{ gap: 10, padding: '12px 14px', borderRadius: 14, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>Guthaben {name}</span>
        <span style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{stundenText(konto)}</span>
      </div>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Wie viele Stunden auszahlen?</span>
        <input autoFocus inputMode="decimal" value={eingabe} onChange={(e) => { setEingabe(e.target.value); setFehler(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') los(); }}
          aria-invalid={fehler ? true : undefined}
          style={{ ...FELD, height: 50, borderRadius: 15, fontSize: 18, fontWeight: 800, boxShadow: fehler ? 'inset 0 0 0 1.5px var(--bad)' : 'inset 0 0 0 1px var(--line)' }} />
      </label>
      {fehler && (
        <div role="alert" className="flex items-start" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
          <WarningCircle size={16} weight="fill" className="shrink-0" style={{ marginTop: 1 }} />{fehler}
        </div>
      )}
      {ueber && pruefung.ok && (
        <div role="alert" className="flex items-start" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 700 }}>
          <Warning size={16} weight="fill" className="shrink-0" style={{ marginTop: 1 }} />
          {stundenText(pruefung.minuten)} sind mehr als das Guthaben ({stundenText(konto)}). Trotzdem auszahlen? Das Konto geht dann ins Minus.
        </div>
      )}
      <p className="m-0" style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--ink3)' }}>
        Die Stunden verlassen das Konto zum {zumText} · Bemerkung „Auszahlung {monatText}“.
      </p>
    </DialogHuelle>
  );
}
